import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import { CartProvider } from '@/context/CartContext';
import { RegionProvider } from '@/context/RegionContext';
import { AuthProvider } from '@/context/AuthContext';
import ThemeProvider from '@/components/ThemeProvider';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TrackingScripts from '@/components/TrackingScripts';
import Chatbot from '@/components/Chatbot';
import AuthCallback from '@/components/AuthCallback';
import Home from '@/pages/Home';
import About from '@/pages/About';
import Gallery from '@/pages/Gallery';
import Exhibitions from '@/pages/Exhibitions';
import Press from '@/pages/Press';
import Contact from '@/pages/Contact';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import PrintConfigurator from '@/pages/PrintConfigurator';
import Admin from '@/pages/Admin';
import AdminDashboard from '@/pages/AdminDashboard';
import SiteCustomization from '@/pages/admin/SiteCustomization';
import ChatbotManager from '@/pages/admin/ChatbotManager';
import OrderManagement from '@/pages/admin/OrderManagement';
import ProdigiConfig from '@/pages/admin/ProdigiConfig';
import QikinkConfig from '@/pages/admin/QikinkConfig';
import PageContentEditor from '@/pages/admin/PageContentEditor';
import TestimonialManager from '@/pages/admin/TestimonialManager';
import CommissionManager from '@/pages/admin/CommissionManager';
import AnalyticsDashboard from '@/pages/admin/AnalyticsDashboard';
import ArtworkEditor from '@/pages/admin/ArtworkEditor';
import Blog from '@/pages/Blog';
import BlogPostPage from '@/pages/BlogPost';
import BlogManager from '@/pages/admin/BlogManager';
import TrackOrder from '@/pages/TrackOrder';
import Profile from '@/pages/Profile';
import DiscountManager from '@/pages/admin/DiscountManager';
import CollectionManager from '@/pages/admin/CollectionManager';
import Collections from '@/pages/Collections';
import CollectionDetail from '@/pages/CollectionDetail';
import Testimonials from '@/pages/Testimonials';
import Commissions from '@/pages/Commissions';
import Portraits from '@/pages/Portraits';
import MarketingDashboard from '@/pages/admin/MarketingDashboard';
import PrintAnalytics from '@/pages/admin/PrintAnalytics';
import BulkImageEditor from '@/pages/admin/BulkImageEditor';
import ReviewManager from '@/pages/admin/ReviewManager';
import ArtworkDetail from '@/pages/ArtworkDetail';
import Privacy from '@/pages/Privacy';
import NotFound from '@/pages/NotFound';
import FeaturedOrder from '@/pages/admin/FeaturedOrder';
import { Toaster } from '@/components/ui/sonner';
import RegionPicker from '@/components/RegionPicker';
import PageTracker from '@/components/PageTracker';
import ImageProtection from '@/components/ImageProtection';

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div key={location.pathname} variants={pageVariants} initial="initial" animate="animate" exit="exit">
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/exhibitions" element={<Exhibitions />} />
          <Route path="/press" element={<Press />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/commissions" element={<Commissions />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/print/:artworkId" element={<PrintConfigurator />} />
          <Route path="/artwork/:id" element={<ArtworkDetail />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/customize" element={<SiteCustomization />} />
          <Route path="/admin/chatbot" element={<ChatbotManager />} />
          <Route path="/admin/orders" element={<OrderManagement />} />
          <Route path="/admin/prodigi" element={<ProdigiConfig />} />
          <Route path="/admin/qikink" element={<QikinkConfig />} />
          <Route path="/admin/pages" element={<PageContentEditor />} />
          <Route path="/admin/testimonials" element={<TestimonialManager />} />
          <Route path="/admin/commissions" element={<CommissionManager />} />
          <Route path="/admin/marketing" element={<MarketingDashboard />} />
          <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
          <Route path="/admin/print-analytics" element={<PrintAnalytics />} />
          <Route path="/admin/bulk-editor" element={<BulkImageEditor />} />
          <Route path="/admin/reviews" element={<ReviewManager />} />
          <Route path="/admin/artwork/:artworkId" element={<ArtworkEditor />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/admin/blog" element={<BlogManager />} />
          <Route path="/admin/discounts" element={<DiscountManager />} />
          <Route path="/admin/collections" element={<CollectionManager />} />
          <Route path="/admin/featured" element={<FeaturedOrder />} />
          <Route path="/collections" element={<Collections />} />
          <Route path="/collections/:collectionId" element={<CollectionDetail />} />
          <Route path="/portraits" element={<Portraits />} />
          <Route path="/portraits/:type" element={<Portraits />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

function App() {
  return (
    <HelmetProvider>
    <RegionProvider>
      <CartProvider>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <div className="App min-h-screen flex flex-col">
                <PageTracker />
                <TrackingScripts />
                <ImageProtection />
                <Navigation />
                <main className="flex-1">
                  <AnimatedRoutes />
                </main>
                <Footer />
                <Chatbot />
                <Toaster />
                <RegionPicker />
              </div>
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </CartProvider>
    </RegionProvider>
    </HelmetProvider>
  );
}

export default App;
